import React, {
  FunctionComponent,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { TfiMoreAlt } from "react-icons/tfi"
import { useLocation, useParams } from "react-router-dom"
import { useNavigate } from "react-router-dom"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import BottomAction from "@fuse/sharedComponents/buttons/BottomAction"
import SolidBtn from "@fuse/sharedComponents/buttons/SolidBtn"
import EventFeedback from "@fuse/sharedComponents/EventFeedback"
import { useSignUpModalState } from "@fuse/sharedComponents/globalQuickSignUpModal"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import MapWebView from "@fuse/sharedComponents/map"
import Navbar from "@fuse/sharedComponents/navbar/Navbar"
import ProgressiveImage from "@fuse/sharedComponents/progressiveImage/ProgressiveImage"
import { RenderHtml } from "@fuse/sharedComponents/renderHtml"
import RestrictionModal from "@fuse/sharedComponents/restrictionModal"
import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import VideoLoader from "@fuse/sharedComponents/videoLoader"
import {
  EButtonType,
  EEventStatus,
  EFeatureFlags,
  EVENT_CLASS,
  EVENT_STATUS,
  EventDocData,
  EventStatusType,
  TEventDetail,
} from "@letshang/constants"
import { getMetadata, ref } from "firebase/storage"
import { get, isEmpty } from "lodash-es"
import { useRecoilState, useRecoilValue } from "recoil"
import { ROUTES } from "src/App"

import { eventMetadataState } from "@fuse/recoil/events"
import { allEventCacheState } from "@fuse/recoil/events"
import { userState } from "@fuse/recoil/signup"

import { useProcessFeatureFlags } from "@settingsAlias/featureFlags"
import momentTz from "@settingsAlias/momentInstance"

import { setPlusOnes } from "@api/firebaseWrappedApis"

import { CancelPublishedEventModal } from "@fuse/pages/eventPage/components/cancelEvent"
import EmailVerificationPopup from "@fuse/pages/eventPage/components/EmailVerificationPopup"
import NonTicketedEventReasonPopup from "@fuse/pages/eventPage/components/NonTicketedEventReasonPopup"
import PlusOnesPopup from "@fuse/pages/eventPage/components/PlusOnesPopup"
import EventError from "@fuse/pages/eventPage/Modal/Error"
import EventNotFound, {
  EEventModalButtons,
  EModalShowContentMap,
  EventModalContentMap,
  useShowAlertWithContentType,
} from "@fuse/pages/eventPage/Modal/EventNotFound"

import { getCustomMetadata } from "../eventSignUp/eventHelper"
import { useGuestListData } from "../guestList/useGuestListData"
import BuddiesGuestList, {
  BuddiesGuestListHandle,
} from "./components/BuddiesGuestList"
import { useEvents } from "./controller"
import EventBanner2 from "./EventBanner2"
import EventDetails from "./EventDetails"
import { SettingsBottomSheetHandle } from "./eventManagement-hackathon-4/SettingsBottomSheet"
import SettingsBottomSheet from "./eventManagement-hackathon-4/SettingsBottomSheet"
import FastSignUpComponent from "./FastSignUpComponent"
import "./style.css"

// Custom hook for scroll synchronization
const useScrollSynchronization = (eventDetails?: TEventDetail) => {
  const [
    isDetailsContainerScrolledToBottom,
    setIsDetailsContainerScrolledToBottom,
  ] = useState(false)
  const [isDetailsContainerScrolledToTop, setIsDetailsContainerScrolledToTop] =
    useState(true)
  const [isMainPageScrollEnabled, setIsMainPageScrollEnabled] = useState(false)
  const detailsContainerRef = useRef<HTMLDivElement>(null)
  const mainPageRef = useRef<HTMLDivElement>(null)

  const handleMainPageScroll = useCallback((e: WheelEvent) => {
    if (!detailsContainerRef.current) {
      // If details container is not available, allow normal scrolling
      return
    }

    const detailsContainer = detailsContainerRef.current

    // Check if the details container has scrollable content
    const hasScrollableContent =
      detailsContainer.scrollHeight > detailsContainer.clientHeight

    if (!hasScrollableContent) {
      // If no scrollable content, allow main page scroll
      return
    }

    const isAtTop = detailsContainer.scrollTop <= 0
    const isAtBottom =
      detailsContainer.scrollTop + detailsContainer.clientHeight >=
      detailsContainer.scrollHeight - 1

    const scrollAmount = e.deltaY

    // Determine scroll direction
    const isScrollingDown = scrollAmount > 0
    const isScrollingUp = scrollAmount < 0

    if (isScrollingDown && !isAtBottom) {
      // Scrolling down and not at bottom - scroll details container
      e.preventDefault()
      e.stopPropagation()
      detailsContainer.scrollTop += scrollAmount
    } else if (isScrollingUp && !isAtTop) {
      // Scrolling up and not at top - scroll details container
      e.preventDefault()
      e.stopPropagation()
      detailsContainer.scrollTop += scrollAmount
    } else if (isScrollingDown && isAtBottom) {
      // Scrolling down and at bottom - allow main page scroll down
      setIsDetailsContainerScrolledToBottom(true)
      setIsMainPageScrollEnabled(true)
    } else if (isScrollingUp && isAtTop) {
      // Scrolling up and at top - allow main page scroll up
      setIsDetailsContainerScrolledToTop(true)
      setIsMainPageScrollEnabled(true)
    }
  }, [])

  const handleDetailsContainerScroll = useCallback(() => {
    if (!detailsContainerRef.current) return

    const detailsContainer = detailsContainerRef.current

    // Check if the details container has scrollable content
    const hasScrollableContent =
      detailsContainer.scrollHeight > detailsContainer.clientHeight

    if (!hasScrollableContent) {
      // If no scrollable content, enable main page scroll
      setIsDetailsContainerScrolledToBottom(true)
      setIsMainPageScrollEnabled(true)
      return
    }

    const isAtTop = detailsContainer.scrollTop <= 0
    const isAtBottom =
      detailsContainer.scrollTop + detailsContainer.clientHeight >=
      detailsContainer.scrollHeight - 1

    // Update scroll state based on position
    setIsDetailsContainerScrolledToBottom(isAtBottom)
    setIsDetailsContainerScrolledToTop(isAtTop)
    setIsMainPageScrollEnabled(isAtTop || isAtBottom)
  }, [])

  useEffect(() => {
    const mainPage = mainPageRef.current
    if (!mainPage) return

    const handleScroll = (e: WheelEvent) => {
      // Always handle scroll events to determine direction
      handleMainPageScroll(e)
    }

    mainPage.addEventListener("wheel", handleScroll, { passive: false })

    return () => {
      mainPage.removeEventListener("wheel", handleScroll)
    }
  }, [handleMainPageScroll, eventDetails])

  useEffect(() => {
    const detailsContainer = detailsContainerRef.current
    if (!detailsContainer) return

    detailsContainer.addEventListener("scroll", handleDetailsContainerScroll)

    return () => {
      detailsContainer.removeEventListener(
        "scroll",
        handleDetailsContainerScroll,
      )
    }
  }, [handleDetailsContainerScroll, eventDetails])

  // Reset scroll state when event changes
  useEffect(() => {
    setIsDetailsContainerScrolledToBottom(false)
    setIsDetailsContainerScrolledToTop(true)
    setIsMainPageScrollEnabled(false)
  }, [])

  return {
    detailsContainerRef,
    mainPageRef,
    isDetailsContainerScrolledToBottom,
    isDetailsContainerScrolledToTop,
    isMainPageScrollEnabled,
  }
}

export const EventLandingPage: FunctionComponent = () => {
  const eventInvitationRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const params = useParams()
  const eventId = get(params, "eventId", "")
  const [user] = useRecoilState(userState)

  const location = useLocation()
  const from = get(location, "state.from", "")
  const urlParams = new URLSearchParams(window.location.search)
  const invitedId = urlParams.get("invitedId")
  const { showContentTypeAlert } = useShowAlertWithContentType()
  const { showFastSignUpPopUp, setShowFastSignUpPopUp } = useSignUpModalState()
  const [storedMetadata, setStoredMetadata] = useRecoilState(eventMetadataState)
  const cachedEvent = useRecoilValue(allEventCacheState)
  const { guestList: guestlist } = useGuestListData(eventId)

  const guestListWeb = {
    isAwaitingEnabled: useProcessFeatureFlags(
      EFeatureFlags.GUEST_LIST_WEB_AWAITING,
    ),
    isInvitedEnabled: useProcessFeatureFlags(
      EFeatureFlags.GUEST_LIST_WEB_INVITED,
    ),
    isGoingEnabled: useProcessFeatureFlags(EFeatureFlags.GUEST_LIST_WEB_GOING),
    isNotGoingEnabled: useProcessFeatureFlags(
      EFeatureFlags.GUEST_LIST_WEB_NOT_GOING,
    ),
  }

  const isAnyGuestListWebEnabled = Object.values(guestListWeb).some(Boolean)

  // Scroll synchronization hook

  const {
    isLoading,
    eventDetails,
    eventType,
    hasNotJoined,
    isAdmin,
    myEventActions,
    isDeclined,
    isRefunded,
    isPageLoading,
    onLeaveEvent,
    canLeaveEvent,
    onLeaveNonTicketedEvent,
    isNonTicketedReasonPopupOpen,
    onConfirmLeaveNonTicketedEvent,
    handleCloseNonTicketedReasonPopup,
    isUserEmailExist,
    isEmailInboxVerification,
    onVerifyEmail,
    loadingEmailVerification,
    isVerificationPopup,
    setIsVerificationPopup,
    setIsEmailInboxVerification,
    myTicketStatus,
    refundAlertVisible,
    refundReferenceNumber,
    refresh,
    isDraftEvent,
    plusOnePopupState,
    setPlusOnePopupState,
    plusOnes,
    acceptedGuestAvatars,
    onEventJoin,
    guestList,
    state,
    isOpenGuestList,
    setIsOpenGuestList,
    myJoinStatus,
    isCancelEventModalOpen,
    handleOpenCancelModal,
    handleCloseCancelModal,
    handleEventCancelled,
  } = useEvents(eventId, get(user, "uid"), invitedId)
  const showContentTypeAlertRef = useRef(showContentTypeAlert)
  const [metadata, setMetadata] = useState(
    isEmpty(storedMetadata[eventId]) ? null : storedMetadata[eventId],
  )
  const [showLoader, setShowLoader] = useState(false)
  const [loading, setLoading] = useState(
    isEmpty(storedMetadata[eventId]) ? true : false,
  )
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)

  useEffect(() => {
    if (!storedMetadata) {
      if (cachedEvent[eventId] && !cachedEvent[eventId].hasError) {
        setLoading(false)
      } else {
        setLoading(true)
        // setShowLoader(true)
      }
    }
    // Fetch latest metadata in the background
    getCustomMetadata(eventId).then((freshMetadata) => {
      if (freshMetadata) {
        setMetadata(freshMetadata)
        console.log("FRESH METADATA", freshMetadata)
        // Update localStorage
        const updatedMetadata = {
          ...storedMetadata,
          [eventId]: freshMetadata,
        }
        setStoredMetadata(updatedMetadata)
      }
    })
    if (
      cachedEvent[eventId] &&
      !cachedEvent[eventId].hasError &&
      !isEmpty(cachedEvent[eventId].eventDetails)
    )
      setLoading(false)
  }, [eventId])

  useEffect(() => {
    if (showLoader) {
      if (!isLoading && cachedEvent[eventId]) {
        setLoading(false)
      } else if (!isLoading && (state?.doesNotExist || state?.hasError)) {
        setLoading(false)
      }
    }
  }, [isLoading, cachedEvent, state, eventId, showLoader])

  const eventDocData: EventDocData =
    get(eventDetails, "eventDocData", null) ||
    get(cachedEvent[eventId], "eventDetails.eventDocData", null)
  const flyerImage =
    cachedEvent[eventId]?.eventDetails?.eventDocData?.flyerImage ||
    get(eventDocData, "flyerImage", "")
  // console.log(
  //   "cached on event page >>>>>>>",
  //   eventDetails?.eventDocData?.flyerImage,
  //   cachedEvent[eventId],
  // )
  const eventStatusData = get(
    eventDocData,
    "eventStatus",
    EEventStatus.UPCOMING,
  )
  const eventClass = get(
    eventDocData,
    "eventClass",
    EVENT_CLASS.NON_TICKETED_EVENT,
  )
  const isTicketedEvent = eventClass === EVENT_CLASS.TICKETED_EVENT
  const isEventExist = !isEmpty(eventDocData) || !isEmpty(eventType)

  const [eventStatus, setEventStatus] = useState(eventType)

  const onEventDetails = useCallback(() => {
    eventInvitationRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }, [])

  const onHomePage = useCallback(() => {
    navigate(ROUTES.LANDING)
  }, [navigate])

  // Handle refresh after Stripe checkout
  useEffect(() => {
    if (from === "StripeCheckout") {
      refresh()
    }
  }, [from, refresh])

  // Scroll to event details and set event status
  useEffect(() => {
    onEventDetails()
    if (isEventExist) {
      setEventStatus(eventType ?? EEventStatus.UPCOMING)
    } else if (eventType) {
      setEventStatus(eventType)
    }
  }, [isEventExist, eventType, onEventDetails])

  // Handle event closed or cancelled alerts
  useEffect(() => {
    if (
      (eventStatusData as EEventStatus) === EEventStatus.CLOSE &&
      !refundAlertVisible
    ) {
      if (!hasNotJoined) {
        showContentTypeAlertRef.current({
          ...EventModalContentMap[EModalShowContentMap.CANCELLED],
          description: EventModalContentMap.cancelled.description.replace(
            "{{eventName}}",
            get(eventDocData, "eventName", ""),
          ),
        })
      } else {
        showContentTypeAlertRef.current({
          ...EventModalContentMap.closed,
          buttons: [
            // EEventModalButtons.DOWNLOAD_APP,
            EEventModalButtons.GO_TO_HOMEPAGE,
          ],
        })
      }
    }

    if (refundAlertVisible) {
      showContentTypeAlertRef.current({
        ...EventModalContentMap.refundProcessed,
        description: React.cloneElement(
          EventModalContentMap.refundProcessed.description,
          {
            children: React.Children.map(
              EventModalContentMap.refundProcessed.description.props.children,
              (child) => {
                if (
                  typeof child === "object" &&
                  get(child, "props.className") ===
                    "flex mb-4 w-[100%] justify-center items-center"
                ) {
                  return React.cloneElement(child, {
                    children: React.Children.map(
                      get(child, "props.children", []),
                      (grandChild) => {
                        if (get(grandChild, "type") === "p") {
                          return React.cloneElement(grandChild, {
                            children: refundReferenceNumber,
                          })
                        }
                        return grandChild
                      },
                    ),
                  })
                }
                return child
              },
            ),
          },
        ),
      })
    }

    if (isRefunded) {
      showContentTypeAlertRef.current({
        ...EventModalContentMap.refunded,
        description: React.cloneElement(
          EventModalContentMap.refunded.description,
          {
            children: React.Children.map(
              EventModalContentMap.refunded.description.props.children,
              (child) => {
                if (
                  typeof child === "object" &&
                  get(child, "props.className") ===
                    "flex mb-4 w-[100%] justify-center items-center"
                ) {
                  return React.cloneElement(child, {
                    children: React.Children.map(
                      get(child, "props.children", []),
                      (grandChild) => {
                        if (get(grandChild, "type") === "p") {
                          return React.cloneElement(grandChild, {
                            children: refundReferenceNumber,
                          })
                        }
                        return grandChild
                      },
                    ),
                  })
                }
                return child
              },
            ),
          },
        ),
      })
    }
  }, [
    eventStatusData,
    refundAlertVisible,
    eventDocData,
    hasNotJoined,
    refundReferenceNumber,
    isRefunded,
  ])

  // Handle event status alerts
  useEffect(() => {
    if (loading) return

    if (eventStatus === EEventStatus.INELIGIBLE) {
      showContentTypeAlertRef.current({
        ...EventModalContentMap[EModalShowContentMap.RESTRICTED],
        buttons: [
          // EEventModalButtons.DOWNLOAD_APP,
          EEventModalButtons.GO_TO_HOMEPAGE,
        ],
      })
    } else if (
      eventStatus === EEventStatus.OVER ||
      eventStatus === EEventStatus.CLOSED
    ) {
      if (hasNotJoined) {
        showContentTypeAlertRef.current({
          ...EventModalContentMap[EModalShowContentMap.CLOSED],
          buttons: [
            // EEventModalButtons.DOWNLOAD_APP,
            EEventModalButtons.GO_TO_HOMEPAGE,
          ],
        })
      }
    }
  }, [eventStatus, hasNotJoined, isLoading])

  // Handle ticket status alerts
  useEffect(() => {
    if (myTicketStatus === EventStatusType.DECLINED) {
      showContentTypeAlertRef.current({
        ...EventModalContentMap[EModalShowContentMap.DECLINED],
        buttons: [
          // EEventModalButtons.DOWNLOAD_APP
        ],
      })
    }
  }, [myTicketStatus])

  // Handle declined event alerts
  useEffect(() => {
    if (isDeclined) {
      showContentTypeAlertRef.current({
        ...EventModalContentMap[EModalShowContentMap.REQ_DECLINED],
        description: get(
          EventModalContentMap[EModalShowContentMap.REQ_DECLINED],
          "description",
          "",
        ).replace("{{eventName}}", get(eventDocData, "eventName", "")),
        buttons: [
          // EEventModalButtons.DOWNLOAD_APP
        ],
      })
    }
  }, [isDeclined, eventDocData])

  const handlePlusOneSubmit = useCallback(
    (count: number) => {
      setPlusOnePopupState((prev) => ({
        ...prev,
        plusOneCount: count,
        isOpen: false,
      }))

      setPlusOnes({ eventId, count })
        .then((res) => {
          if (!res?.success) {
            showErrorToast(get(res, "message", res?.message || "Unknown error"))
            return
          }
          showSuccessToast(get(res, "message", "Successfully updated +1s"))
        })
        .catch((err) => {
          showErrorToast(`Error: ${get(err, "message", "Unknown error")}`)
        })
    },
    [eventId, setPlusOnePopupState],
  )

  const handlePlusOneChange = useCallback(
    (count: number) => {
      setPlusOnePopupState((prev) => ({
        ...prev,
        plusOneCount: count,
      }))
    },
    [setPlusOnePopupState],
  )

  const handlePlusOneClose = useCallback(() => {
    setPlusOnePopupState((prev) => ({
      ...prev,
      isOpen: false,
    }))
  }, [setPlusOnePopupState])

  const handleVerificationClose = useCallback(() => {
    setIsVerificationPopup(false)
    setIsEmailInboxVerification(false)
  }, [setIsVerificationPopup, setIsEmailInboxVerification])

  const onEventAction = (index: number | undefined) => {
    if (index === undefined) return
    const action = myEventActions[index]
    if (!action) return

    if ("onPress" in action) {
      action.onPress()
    }
  }

  const hasEventEnded = momentTz()
    .tz(get(eventDocData, "timing.displayTimezone", "America/New_York"))
    .isAfter(
      momentTz(get(eventDocData, "timing.eventEndDate"))
        .tz(get(eventDocData, "timing.displayTimezone", "America/New_York"))
        .hour(momentTz(get(eventDocData, "timing.eventEndTime")).hour())
        .minute(momentTz(get(eventDocData, "timing.eventEndTime")).minute()),
    )
  const showEdit = useProcessFeatureFlags(EFeatureFlags.EDIT_EVENT)

  useEffect(() => {
    if (!metadata && isLoading && !cachedEvent[eventId]) setShowLoader(true)
  }, [cachedEvent, isLoading, loading, metadata, eventId])

  const hasJoined = hasNotJoined !== undefined ? !hasNotJoined : false

  const settingsBottomSheetRef = useRef<SettingsBottomSheetHandle>(null)
  const onOpenSettingsBottomSheet = () => {
    settingsBottomSheetRef.current?.openModal()
  }

  const buddiesGuestListRef = useRef<BuddiesGuestListHandle>(null)
  const onOpenBuddiesGuestList = () => {
    buddiesGuestListRef.current?.openModal()
  }

  const { detailsContainerRef, mainPageRef } =
    useScrollSynchronization(eventDetails)

  //not allow edit event if event is closed or cancelled, it's a quick fix, (copied the logic from <EventHeader/>)
  const isEventClosed =
    (!hasNotJoined && eventStatus === EEventStatus.CLOSED) ||
    String(eventStatusData) === String(EEventStatus.CLOSE)

  return (
    <>
      {showLoader && (
        <VideoLoader loading={loading} setShowLoader={setShowLoader} />
      )}

      {state?.doesNotExist && <EventNotFound userId={user?.uid} />}
      {state?.hasError && <EventError />}

      {metadata && !cachedEvent[eventId] && (
        <EventDetails
          showCached={true}
          cachedDetails={{ ...metadata, showFlyer: false }}
          isOpenGuestList={isOpenGuestList}
          setIsOpenGuestList={setIsOpenGuestList}
          myJoinStatus={myJoinStatus}
          onFeedbackModalStateChange={setIsFeedbackModalOpen}
          detailsContainerRef={detailsContainerRef}
        />
      )}

      {cachedEvent[eventId] &&
        !cachedEvent[eventId].hasError &&
        !isEmpty(cachedEvent[eventId].eventDetails) &&
        !metadata &&
        isLoading && (
          <EventDetails
            showCached={true}
            cachedDetails={{
              eventName:
                cachedEvent[eventId]?.eventDetails?.eventDocData?.eventName,
              eventDate:
                cachedEvent[eventId]?.eventDetails?.eventDocData?.timing
                  ?.eventDate,
              actualFlyerUrl:
                cachedEvent[eventId]?.eventDetails?.eventDocData?.flyerImage,
              location: JSON.stringify({
                eventLocation: "Jaipur",
                otherLocationDetails: "",
                latitude: 26.94777632365763,
                longitude: 75.78814747795944,
                radius: 1000,
                guestLocationPreview: "",
              }),
              eventDescription:
                cachedEvent[eventId].eventDetails.eventDocData.eventDescription,
              initiatorName:
                cachedEvent[eventId].eventDetails.eventDocData.initiatorName,
              showFlyer: true,
            }}
            isOpenGuestList={isOpenGuestList}
            setIsOpenGuestList={setIsOpenGuestList}
            myJoinStatus={myJoinStatus}
            onFeedbackModalStateChange={setIsFeedbackModalOpen}
            detailsContainerRef={detailsContainerRef}
          />
        )}

      {eventDetails && !isEmpty(eventDetails) ? (
        <BackgroundWrapper bgImage={flyerImage} className={`overflow-auto`}>
          <BlurWrapper
            blurKey={get(blurKeys, "mainBlurView")}
            overlayOpacityDefault="75"
            overlayColorDefault="dark"
            className="min-h-screen flex flex-col overflow-auto"
          >
            <div
              ref={mainPageRef}
              className="min-h-[100vh] h-full flex flex-col overflow-auto"
            >
              <div id="upper-body" className="flex-1">
                <Navbar />

                <div id="body">
                  {isPageLoading && <LoadingSpinner />}

                  <PlusOnesPopup
                    isOpen={get(plusOnePopupState, "isOpen", false)}
                    isLoading={get(plusOnePopupState, "isLoading", false)}
                    plusOneCount={get(plusOnePopupState, "plusOneCount", 0)}
                    isEditMode={get(plusOnePopupState, "isEditMode", false)}
                    plusOneAllowedNumber={get(
                      plusOnes,
                      "allowedNumber",
                      [0, 0],
                    )}
                    showAlert={get(plusOnePopupState, "showAlert", false)}
                    onClose={handlePlusOneClose}
                    onSubmit={handlePlusOneSubmit}
                    onChange={handlePlusOneChange}
                  />
                  <div className="flex-1 ">
                    {isVerificationPopup && (
                      <Suspense fallback={null}>
                        <EmailVerificationPopup
                          isLoading={loadingEmailVerification}
                          setIsEmailInboxVerification={
                            setIsEmailInboxVerification
                          }
                          isEmailInboxVerification={isEmailInboxVerification}
                          onClose={handleVerificationClose}
                          onVerifyEmail={onVerifyEmail}
                        />
                      </Suspense>
                    )}

                    {isNonTicketedReasonPopupOpen && (
                      <Suspense fallback={null}>
                        <NonTicketedEventReasonPopup
                          isLoading={isPageLoading}
                          onClose={handleCloseNonTicketedReasonPopup}
                          onConfirmLeaveNonTicketedEvent={
                            onConfirmLeaveNonTicketedEvent
                          }
                        />
                      </Suspense>
                    )}

                    <div ref={eventInvitationRef}>
                      <EventDetails
                        eventDetails={eventDetails}
                        eventDocData={eventDocData}
                        hasNotJoined={hasNotJoined}
                        isAdmin={isAdmin}
                        myEventActions={myEventActions}
                        onLeaveEvent={
                          isTicketedEvent
                            ? onLeaveEvent
                            : onLeaveNonTicketedEvent
                        }
                        myTicketStatus={myTicketStatus}
                        canLeaveEvent={canLeaveEvent}
                        isUserEmailExist={isUserEmailExist}
                        acceptedGuestAvatars={acceptedGuestAvatars as []}
                        openEmailVerification={() =>
                          setIsVerificationPopup(true)
                        }
                        eventStatus={eventStatus}
                        isDraftEvent={isDraftEvent}
                        onEventJoin={onEventJoin}
                        guestList={guestList}
                        flyerImage={flyerImage}
                        invitedId={invitedId}
                        onEventAction={onEventAction}
                        isOpenGuestList={isOpenGuestList}
                        setIsOpenGuestList={setIsOpenGuestList}
                        hasEventEnded={hasEventEnded}
                        myJoinStatus={myJoinStatus}
                        onOpenSettingsBottomSheet={onOpenSettingsBottomSheet}
                        onOpenBuddiesGuestList={onOpenBuddiesGuestList}
                        onFeedbackModalStateChange={setIsFeedbackModalOpen}
                        detailsContainerRef={detailsContainerRef}
                        isAnyGuestListWebEnabled={isAnyGuestListWebEnabled}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {isEventExist && (
                <div id="lower-body" className="portrait:mb-20">
                  <span className="hidden">
                    <Suspense fallback={null}>
                      <EventBanner2 eventId={eventId} flyerImage={flyerImage} />
                    </Suspense>
                  </span>
                  {/* Bottom component disabled - footer is now integrated in EventDetails.tsx */}
                  {/* <span>
                  <Suspense fallback={null}>
                    <Bottom isEventDetailFooter={true} isAdmin={isAdmin} />
                  </Suspense>
                </span> */}
                </div>
              )}
            </div>
          </BlurWrapper>
          {isCancelEventModalOpen && (
            <Suspense fallback={null}>
              <CancelPublishedEventModal
                isOpen={isCancelEventModalOpen}
                onClose={handleCloseCancelModal}
                eventId={eventId}
                eventName={get(eventDocData, "eventName", "")}
                onEventCancelled={handleEventCancelled}
              />
            </Suspense>
          )}

          <div
            className={`fixed w-full bottom-0  p-4 landscape:hidden flex flex-row gap-4 ${
              isFeedbackModalOpen ? "hidden" : ""
            }`}
            // style={{
            //   backdropFilter: "blur(10px)",
            //   background: "#00000080",
            // }}
          >
            {isAdmin &&
              eventDocData?.eventStatus === EVENT_STATUS.OPEN &&
              !hasEventEnded &&
              showEdit && (
                <div className={` sm:w-auto `}>
                  <SolidBtn
                    size="lg"
                    variant={EButtonType.solid}
                    disabled={false}
                    onClick={onOpenSettingsBottomSheet}
                    className={`text-white opacity-100 px-10 w-full sm:w-auto flex-1 rounded-lg backdrop-blur-md py-5 h-full`}
                  >
                    <TfiMoreAlt />
                  </SolidBtn>
                </div>
              )}
            {!isEmpty(myEventActions) && (
              <div className="flex-1">
                <BottomAction
                  // bottomActionClassName="mt-6"
                  bottomActionClassName={"w-full"}
                  buttonWrapperClassName={"flex-1"}
                  solidBtnClassName={
                    "flex-1 !flex py-5 backdrop-blur-md rounded-lg"
                  }
                  buttonOptionList={myEventActions}
                  handleScreen={onEventAction}
                />
              </div>
            )}
          </div>

          <FastSignUpComponent
            isOpen={showFastSignUpPopUp}
            setOpenState={setShowFastSignUpPopUp}
            title={get(eventDocData, "eventName", "-")}
          />
          <SettingsBottomSheet
            ref={settingsBottomSheetRef}
            eventId={eventId}
            eventDocData={eventDocData}
            onGuestList={onOpenBuddiesGuestList}
            eventDetails={eventDetails}
            allowEdit={!isEventClosed}
            onCancelEvent={handleOpenCancelModal}
            isAnyGuestListWebEnabled={isAnyGuestListWebEnabled}
          />
          <BuddiesGuestList
            ref={buddiesGuestListRef}
            onClose={() => setIsOpenGuestList(false)}
            onJoinEvent={onEventJoin}
            hasJoined={hasJoined}
            guestList={guestList}
            isAdmin={isAdmin}
            acceptedGuestAvatars={acceptedGuestAvatars}
            isTicketedEvent={isTicketedEvent}
            myJoinStatus={myJoinStatus}
            eventId={eventId}
          />
        </BackgroundWrapper>
      ) : null}
    </>
  )
}
